import { defineComponent as _defineComponent } from 'vue'
import {computed, onBeforeMount, ref} from "vue";
import {PieChart} from "echarts/charts";
import VChart from "vue-echarts";
import {use} from "echarts/core";
import {SVGRenderer} from "echarts/renderers";
import {LegendComponent, TitleComponent, TooltipComponent} from "echarts/components";
import VoteModal from "@/components/governance/VoteModal.vue";
import Icon from "../features/IconComponent.vue";
import {Proposal, ProposalDetailsTally, ProposalStatus} from "@/models/store/proposal";
import {useConfigurationStore} from "@/store/configuration.store";
import {createProposalDetailsChartData} from "@/charts/governance";
import {useProposalsStore} from "@/store/proposals.store";
import ShadowedSvgChart from "../commons/ShadowedSvgChart.vue";
import CoinAmount from "../commons/CoinAmount.vue";
import PercentsView from "@/components/commons/PercentsView.vue";
import GovernanceIcon from "../commons/GovernanceIcon.vue";
import {useTokensStore} from "@/store/tokens.store";
import {BigIntWrapper} from "@/models/store/common";
import ProgressBarComponent from "@/components/features/ProgressBarComponent.vue";
import dataService from "@/services/data.service";


export default /*#__PURE__*/_defineComponent({
  __name: 'DetailsChart',
  props: {
    proposal: { type: Object, required: false },
    proposalDetailsTally: { type: Object, required: false }
  },
  setup(__props: any, { expose: __expose }) {
  __expose();


const props = __props;

use([
  SVGRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

const tokensStore = useTokensStore();

onBeforeMount(async () => {
  if (props.proposal?.status && props.proposal.status !== ProposalStatus.VOTING_PERIOD) {
    await dataService.onProposalUpdateVotes(props.proposal.proposalId);
  }
});

function wrapBigInt(value :bigint| undefined){
  return value!==undefined ? new BigIntWrapper(value) : 0;
}

if(props.proposal?.status === ProposalStatus.VOTING_PERIOD) {
  setInterval(() => {
    updateVotes();
  },useConfigurationStore().getConfig.proposalVotingRefreshTimeout);
}

const childRef = ref<InstanceType<typeof ProgressBarComponent>>();


const icons  = new Map<string, string>([
  [ProposalStatus.PASSED, 'CheckSquare'],
  [ProposalStatus.REJECTED, 'XCircle'],
  [ProposalStatus.VOTING_PERIOD, ''],
  [ProposalStatus.UNSPECIFIED, ''],
  [ProposalStatus.DEPOSIT_PERIOD, ''],
  [ProposalStatus.FAILED, ''],
]);

const proposalsStore = useProposalsStore();
const sumOfVotes = computed(() => {
  const val = useProposalsStore().getProposalDetailsTally?.totalChart;
  return (val && val > 0) ? val : -1n;
});

const updateVotes = async () => {
  if(props.proposal?.proposalId) {
    console.log('refresh');
    await dataService.onProposalUpdateVotes(props.proposal.proposalId);
  }
  childRef.value?.startFillingBar();
};

const yes = computed(() => {
  const res = props.proposal?.status === ProposalStatus.VOTING_PERIOD ?
    proposalsStore.proposalTally?.yes : proposalsStore.getProposalDetailsTally?.getYes();
  if(res != undefined) {
    return res;
  }
  return undefined;
});

const no = computed(() => {
  const res = props.proposal?.status === ProposalStatus.VOTING_PERIOD ?
    proposalsStore.proposalTally?.no : proposalsStore.getProposalDetailsTally?.getNo();
  if(res != undefined) {
    return res;
  }
  return undefined;
});

const abstain = computed(() => {
  const res = props.proposal?.status === ProposalStatus.VOTING_PERIOD ?
    proposalsStore.proposalTally?.abstain : proposalsStore.getProposalDetailsTally?.getAbstain();
  if(res != undefined) {
    return res;
  }
  return undefined;
});

const noWithVeto = computed(() => {
  const res = props.proposal?.status === ProposalStatus.VOTING_PERIOD ?
    proposalsStore.proposalTally?.noWithVeto : proposalsStore.getProposalDetailsTally?.getNoWithVeto();
  if(res != undefined) {
    return res;
  }
  return undefined;
});

const notVoted = computed(() => {
  const total = proposalsStore.proposalTally?.total != undefined ? proposalsStore.proposalTally.total : 0n;
  const res = props.proposal?.status === ProposalStatus.VOTING_PERIOD ?
    useTokensStore().getStakingPool.bondedTokens - total : proposalsStore.getProposalDetailsTally?.getNotVoted();
  if(res != undefined) {
    return res;
  }
  return undefined;
});

const yesPercentage = computed(() => {
  return props.proposal?.status === ProposalStatus.VOTING_PERIOD ?
    proposalsStore.proposalTally?.getYesPercentage() : useProposalsStore().getProposalDetailsTally?.getYesPercentage();
});

const noPercentage = computed(() => {
  return props.proposal?.status === ProposalStatus.VOTING_PERIOD ?
    proposalsStore.proposalTally?.getNoPercentage() : useProposalsStore().getProposalDetailsTally?.getNoPercentage();
});

const abstainPercentage = computed(() => {
  return props.proposal?.status === ProposalStatus.VOTING_PERIOD ?
    proposalsStore.proposalTally?.getAbstainPercentage() : useProposalsStore().getProposalDetailsTally?.getAbstainPercentage();
});

const noWithVetoPercentage = computed(() => {
  return props.proposal?.status === ProposalStatus.VOTING_PERIOD ?
    proposalsStore.proposalTally?.getNoWithVetoPercentage() : useProposalsStore().getProposalDetailsTally?.getNoWithVetoPercentage();
});

const totalVotes = computed(() => {
  let total: bigint | undefined;
  if(props.proposal?.status == ProposalStatus.VOTING_PERIOD) {
    total = proposalsStore.proposalTally?.total;
  } else {
    total = useProposalsStore().getProposalDetailsTally?.proposalTally.total;
  }
  return total;
});

const bondedTokens = computed(() => {
  let bonded: bigint | undefined;
  if(props.proposal?.status == ProposalStatus.VOTING_PERIOD) {
    bonded = useTokensStore().getStakingPool.bondedTokens;
  } else {
    bonded = useProposalsStore().getProposalDetailsTally?.stakingPool.bondedTokens;
  }
  return bonded;
});

const option = computed(() => {
  if (yes.value==undefined || abstain.value==undefined || no.value==undefined || noWithVeto.value==undefined || notVoted.value==undefined) {
    return false;
  }

  return createProposalDetailsChartData(useConfigurationStore().config.getConvertedAmount(yes.value),
    useConfigurationStore().config.getConvertedAmount(abstain.value),
    useConfigurationStore().config.getConvertedAmount(no.value),
    useConfigurationStore().config.getConvertedAmount(noWithVeto.value),
    useConfigurationStore().config.getConvertedAmount(notVoted.value),
    sumOfVotes.value);
});

function getProposalTitle() {
  const result = useProposalsStore().getProposal?.content?.title;
  return result ? result : '';
}

function calculatePercents(a, b, precision){
  const result= (a/b) *100;
  return result.toFixed(precision);
}

function getProposalStatus(): ProposalStatus{
  const result = useProposalsStore().getProposal?.status;
  return result ? result : ProposalStatus.UNSPECIFIED;
}


const __returned__ = { props, tokensStore, wrapBigInt, childRef, icons, proposalsStore, sumOfVotes, updateVotes, yes, no, abstain, noWithVeto, notVoted, yesPercentage, noPercentage, abstainPercentage, noWithVetoPercentage, totalVotes, bondedTokens, option, getProposalTitle, calculatePercents, getProposalStatus, get VChart() { return VChart }, VoteModal, Icon, get ProposalStatus() { return ProposalStatus }, get useProposalsStore() { return useProposalsStore }, ShadowedSvgChart, CoinAmount, PercentsView, GovernanceIcon }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})